import { useEffect, useMemo, useState } from 'react'
import { ShieldAlert } from 'lucide-react'
import { ResultBadge } from '../styles'
import type { Creds, LogEntry } from '@features/dashboards/testing-ground/types'
import { extractAmlStep } from '../utils'
import { ConfiguringChecklist } from './ConfiguringChecklist'
import { InteractionDiagram } from './InteractionDiagram'
import { AmlConfigPanel } from './AmlConfigPanel'
import { AmlGuidedView } from './AmlGuidedView'
import { ExperienceModeSwitcher } from './ExperienceModeSwitcher'
import type { ExperienceMode } from './ExperienceModeSwitcher'
import { ExistingCustomerPanel } from './ExistingCustomerPanel'
import type { ExistingCustomerSelection } from './ExistingCustomerPanel'
import type { CustomerMode } from './NewExistingToggle'
import { StageSaveBar } from './StageSaveBar'
import { CONFIG_CHECKLISTS, DEFAULT_AML_CONFIG } from '../data'
import { buildAmlDiagramDsl } from '../dynamicInteractionDiagrams'
import type { PlatformJourneyRecord } from '@api/client'
import type { AmlScreeningConfig, AnyObj } from '../types'
import type { AiConfigureSuggestions, AiStageSuggestionState } from '../aiConfigure'
import { AiSuggestionBanner } from './AiSuggestionBanner'

interface AmlStageProps {
  creds: Creds
  hasCreds: boolean
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
  entityId: string | null
  workflowResult: AnyObj | null
  customerMode: CustomerMode
  existingCustomer: ExistingCustomerSelection | null
  customerName: string | null
  platformVersion: 'v1' | 'v2'
  experienceMode: ExperienceMode
  onExperienceModeChange: (mode: ExperienceMode) => void
  journeyId: number | null
  journeyRecord: PlatformJourneyRecord | null
  onJourneySaved: () => void
  aiSuggestions?: AiConfigureSuggestions | null
  /** Lifted to the tool shell so Account Setup's checklist and this stage's
   * own banner share one apply/dismiss state instead of drifting apart. */
  amlAi: AiStageSuggestionState<Pick<AmlScreeningConfig, 'sanctions' | 'pep' | 'adverseMedia'>>
}

const AML_FACTOR_KEYWORDS = ['pep', 'sanction', 'watchlist', 'adverse media', 'aml']

export function AmlStage({
  creds, hasCreds, addLog, entityId, workflowResult,
  customerMode, existingCustomer, customerName, platformVersion,
  experienceMode, onExperienceModeChange, journeyId, journeyRecord, onJourneySaved,
  aiSuggestions, amlAi,
}: AmlStageProps) {
  const [amlConfig, setAmlConfig] = useState<AmlScreeningConfig>(DEFAULT_AML_CONFIG)
  const [checklistEnabled, setChecklistEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CONFIG_CHECKLISTS.aml.map((i) => [i.id, true]))
  )
  function toggleChecklistItem(id: string) {
    setChecklistEnabled((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  useEffect(() => {
    if (!journeyRecord) return
    if (journeyRecord.amlConfig) setAmlConfig(journeyRecord.amlConfig as AmlScreeningConfig)
    if (journeyRecord.amlChecklist) setChecklistEnabled(journeyRecord.amlChecklist as Record<string, boolean>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyRecord?.id])
  const diagramDsl = useMemo(
    () => buildAmlDiagramDsl({ platformVersion, config: amlConfig }),
    [platformVersion, amlConfig],
  )
  const amlStep = extractAmlStep(workflowResult)
  const riskFactors: AnyObj[] = (workflowResult?.riskAssessment?.riskFactors ?? []).filter((f: AnyObj) => {
    const name = String(f.description ?? f.factor ?? '').toLowerCase()
    return AML_FACTOR_KEYWORDS.some(k => name.includes(k))
  })

  const amlAiBanner = amlAi.visible && aiSuggestions?.aml && (
    <AiSuggestionBanner
      rationale={aiSuggestions.aml.rationale}
      confidence={aiSuggestions.aml.confidence}
      applied={amlAi.applied}
      onApply={amlAi.apply}
      onDismiss={amlAi.dismiss}
    />
  )

  const technicalDetailsNode = (
    <>
      {amlAiBanner}
      <AmlConfigPanel creds={creds} hasCreds={hasCreds} addLog={addLog} entityId={entityId} onConfigChange={setAmlConfig} aiSeed={amlAi.seed} />
      <InteractionDiagram stageId="aml" dsl={diagramDsl} />
      {amlStep && (amlStep.processResults ?? []).length > 0 && (
        <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
          <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
            <ShieldAlert size={13} className="text-violet-700" />
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">AML Screening Result</p>
            {amlStep.result && <ResultBadge result={amlStep.result} />}
          </div>
          <div className="divide-y" style={{ borderColor: '#e5e7eb' }}>
            {(amlStep.processResults as AnyObj[]).map((c, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                <span className="text-[11px] text-[#475569] flex-1 font-mono">{c.objectType ?? c.class ?? '—'}</span>
                <span className="text-[10px] font-mono text-[#64748b] truncate max-w-[180px]">{c.providerResult?.source ?? '—'}</span>
                <ResultBadge result={c.result ?? '—'} />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )

  return (
    <div className="space-y-3">
      {customerMode === 'existing' ? (
        <div className="space-y-3">
          <ExistingCustomerPanel
            productKey="aml"
            sections={['AML']}
            title="AML — Existing Customer"
            description="Shows this customer's AML screening settings as scoped in DealHub. Reads DealHub's own presales record, not FrankieOne's live platform."
            showCascadeSummary={false}
            sharedSelection={existingCustomer}
            searchable={false}
          />
          {amlAiBanner}
          <AmlConfigPanel creds={creds} hasCreds={hasCreds} addLog={addLog} entityId={entityId} onConfigChange={setAmlConfig} aiSeed={amlAi.seed} />
        </div>
      ) : (
      <>
      <ExperienceModeSwitcher mode={experienceMode} onChange={onExperienceModeChange} />
      <StageSaveBar
        journeyId={journeyId}
        buildFields={() => ({ amlConfig, amlChecklist: checklistEnabled })}
        onSaved={onJourneySaved}
      />

      {experienceMode === 'guided' && (
        <AmlGuidedView
          workflowResult={workflowResult}
          amlStep={amlStep}
          riskFactors={riskFactors}
          customerName={customerName}
          checklistItems={CONFIG_CHECKLISTS.aml}
          checklistEnabled={checklistEnabled}
          onToggleChecklistItem={toggleChecklistItem}
          technicalDetails={technicalDetailsNode}
        />
      )}

      {experienceMode === 'classic' && (
      <>
      <ConfiguringChecklist productLabel="AML" items={CONFIG_CHECKLISTS.aml} enabled={checklistEnabled} onToggle={toggleChecklistItem} customerName={customerName} />
      {amlAiBanner}
      <AmlConfigPanel creds={creds} hasCreds={hasCreds} addLog={addLog} entityId={entityId} onConfigChange={setAmlConfig} aiSeed={amlAi.seed} />
      <InteractionDiagram stageId="aml" dsl={diagramDsl} />

      {!workflowResult ? (
        <div className="flex flex-col items-center justify-center py-10 rounded-[14px]" style={{ border: '2px dashed #e5e7eb', background: '#f8fafc' }}>
          <ShieldAlert size={22} className="text-[#94a3b8] mb-3" />
          <p className="text-[13px] font-semibold text-[#64748b]">Run the KYC workflow first</p>
          <p className="text-[11px] text-[#94a3b8] mt-1.5 text-center max-w-sm">
            AML screening runs as part of the same workflow execution — there&apos;s no separate call to trigger here.
          </p>
        </div>
      ) : !amlStep ? (
        <div className="p-4 rounded-[14px]" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
          <p className="text-[12px] text-[#475569]">This workflow didn&apos;t include a dedicated AML step.</p>
        </div>
      ) : (
        <>
          <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
            <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
              <ShieldAlert size={13} className="text-violet-700" />
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">AML Screening Result</p>
              {amlStep.result && <ResultBadge result={amlStep.result} />}
            </div>
            {(amlStep.processResults ?? []).length === 0 ? (
              <p className="px-4 py-3 text-[11px] text-[#475569] italic">No screening hits returned for this step.</p>
            ) : (
              <div className="divide-y" style={{ borderColor: '#e5e7eb' }}>
                {(amlStep.processResults as AnyObj[]).map((c, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                    <span className="text-[11px] text-[#475569] flex-1 font-mono">{c.objectType ?? c.class ?? '—'}</span>
                    <span className="text-[10px] font-mono text-[#64748b] truncate max-w-[180px]">{c.providerResult?.source ?? '—'}</span>
                    <ResultBadge result={c.result ?? '—'} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {riskFactors.length > 0 && (
            <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
              <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">Watchlist / PEP / Sanctions Risk Factors</p>
              </div>
              <div className="p-3 space-y-2">
                {riskFactors.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 rounded-[8px]" style={{ background: '#f8fafc' }}>
                    <span className="text-[11px] text-[#1e293b]">{f.description ?? f.factor}</span>
                    <span className="text-[10px] font-mono font-bold" style={{ color: (f.score ?? 0) > 0 ? '#c2410c' : '#475569' }}>{f.score ?? 0}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      </>
      )}
      </>
      )}
    </div>
  )
}
